import { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from '../../../context/WebSocketContext';

interface DossierUser {
  userId: string;
  socketId: string;
  email: string;
  joinedAt: Date;
}

interface DossierUsersUpdateEvent {
  dossierId: string;
  users: DossierUser[];
  count: number;
}

interface UserJoinedEvent {
  dossierId: string;
  userId: string;
  email: string;
  timestamp: Date;
}

interface UserLeftEvent {
  dossierId: string;
  userId: string;
  timestamp: Date;
}

const useDossierPresence = (dossierId: string | undefined) => {
  const { socket, isConnected } = useWebSocket();
  const [connectedUsers, setConnectedUsers] = useState<DossierUser[]>([]);
  const [userCount, setUserCount] = useState(0);

  // Rejoindre le dossier quand le socket est connecté et qu'on a un dossierId
  useEffect(() => {
    if (!socket || !isConnected || !dossierId) return;

    socket.emit('joinDossier', { dossierId });

    // Quitter le dossier au unmount ou changement de dossier
    return () => {
      socket.emit('leaveDossier', { dossierId });
    };
  }, [socket, isConnected, dossierId]);

  // Écouter les mises à jour des utilisateurs
  useEffect(() => {
    if (!socket || !dossierId) return;

    const handleDossierUsersUpdate = (data: DossierUsersUpdateEvent) => {
      // Vérifier que c'est bien pour notre dossier
      if (data.dossierId === dossierId) {
        setConnectedUsers(data.users);
        setUserCount(data.count);
      }
    };

    const handleUserJoined = (data: UserJoinedEvent) => {
      if (data.dossierId === dossierId) {
      }
    };

    const handleUserLeft = (data: UserLeftEvent) => {
      if (data.dossierId === dossierId) {
      }
    };

    socket.on('dossierUsersUpdate', handleDossierUsersUpdate);
    socket.on('userJoined', handleUserJoined);
    socket.on('userLeft', handleUserLeft);

    return () => {
      socket.off('dossierUsersUpdate', handleDossierUsersUpdate);
      socket.off('userJoined', handleUserJoined);
      socket.off('userLeft', handleUserLeft);
    };
  }, [socket, dossierId]);

  // Fonction pour forcer une mise à jour (utile pour le débogage)
  const refresh = useCallback(() => {
    if (socket && dossierId) {
      socket.emit('leaveDossier', { dossierId });
      setTimeout(() => {
        socket.emit('joinDossier', { dossierId });
      }, 100);
    }
  }, [socket, dossierId]);

  return {
    connectedUsers,
    userCount,
    isConnected,
    refresh,
  };
};

export default useDossierPresence;
